import { defineStore } from "pinia";
import { uniqBy } from "es-toolkit";

import type { ChatState } from "./Models/State";
import type { SendFileQuery } from "./Models/Domain/Chat";
import type { BotStageType } from "@/Modules/Models/Domain/FlexoBot";
import {
  type AllowedChatRoomItemType,
  type ChatRoom,
  type ChatRoomUser,
  type AllChatRoomItemType,
  ChatRoomItemType,
  FileTypes,
} from "./Models/Domain/Chat";
import type { ChatRoomItemMessageWebSocket, TYPING_STATUS } from "./Models/WebSocket";
import type { IonicLocalFile } from "@/Common/Models/Domain/Ionic";

import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { WebSocketManager } from "@/Modules/Chat/WebSocketManager";
import useUserStore from "@/Modules/User/Store";
import useAuthStore from "@/Modules/Auth/Store";

import InitializeBotActionHandler from "./ActionHandlers/InitializeBot";
import getChatEventsActionHandler from "./ActionHandlers/getChatEvents";
import CreateBotStageAnswerActionHandler from "./ActionHandlers/CreateBotStageAnswer";
import SendTextMessageActionHandler from "./ActionHandlers/SendTextMessage";
import SentStatusMessageActionHandler from "./ActionHandlers/SendStatusMessage";
import ConfirmReceiveMessageActionHandler from "./ActionHandlers/ConfirmReceiveMessage";
import LoadChatRoomUserActionHandler from "./ActionHandlers/LoadChatRoomUser";
import SendImageActionHandler from "@/Modules/Chat/ActionHandlers/SendImage";
import SendDocumentActionHandler from "@/Modules/Chat/ActionHandlers/SendDocument";
import SendAudioActionHandler from "@/Modules/Chat/ActionHandlers/SendAudio";
import DownloadFileActionHandler from "@/Modules/Chat/ActionHandlers/DownloadFile";
import SendAllLegalAgreementsToPatientActionHandler from "@/Modules/Chat/ActionHandlers/SendAllLegalAgreementsToPatient";
import ReloadCurrentBreakActionHandler from "@/Modules/Chat/ActionHandlers/ReloadCurrentBreak";
import FetchChatsActionHandler from "@/Modules/Chat/ActionHandlers/FetchChats";
import setActiveChatRoomActionHandler, { SetActiveChatReturn } from "./ActionHandlers/SetActiveChatRoom";
import ReconnectionWebSocketActionHandler from "./ActionHandlers/ReconnectionWebSocket";

import { hasValue } from "@/Utils/Objects";
import { sortChatRooms } from "@/Utils/Chat";

import messageFilters from "./MessageFilters";

const chatState: ChatState = {
  activeChatRoomId: undefined,
  botStage: null,
  isLoadingChats: false,
  isUploadingFile: false,
  audioFiles: {},
  isStartingAnswer: false,
  isReconnecting: false,

  // WebsocketManager
  socketManager: undefined,
  rooms: {},
  openImagePreview: false,
  fileProgress: 0,
  notificationError: false,
  notificationMessage: "",
};

export const useChatStore = defineStore(StateNamespace.CHAT, {
  state: () => ({ ...chatState, userStore: useUserStore(), authStore: useAuthStore() }),
  actions: {
    initializeWS() {
      if (!hasValue(this.socketManager) || !this.socketManager.isOpen()) {
        this.initWebSocket();
      }

      this.initializeBot();
    },

    reconnectedSocket() {
      const handler = new ReconnectionWebSocketActionHandler();

      handler.handle();
    },

    reconnect() {
      if (hasValue(this.socketManager)) {
        this.socketManager.close();
      }

      this.initializeWS();
      this.reconnectedSocket();
    },

    initWebSocket(): boolean {
      if (this.userStore.user && this.authStore.userToken) {
        if (!hasValue(this.socketManager) || this.socketManager.isClosed()) {
          this.socketManager = new WebSocketManager(
            {
              userId: this.userStore.user.id,
              userToken: this.authStore.userToken,
            },
            this.initializeBot,
            this.setAnswerLoading,
            this.reconnectedSocket,
            this.processEventMessage
          );
        } else {
          this.socketManager.initWebsocketConnection(true);
        }

        return true;
      }

      return false;
    },

    closeWebSocket() {
      this.socketManager?.close();
    },

    clear() {
      this.closeWebSocket();

      this.activeChatRoomId = undefined;
      this.botStage = null;
      this.isLoadingChats = false;
      this.isUploadingFile = false;
      this.audioFiles = {};
      this.isStartingAnswer = false;
      this.rooms = {};
      this.socketManager = undefined;
      this.openImagePreview = false;
      this.imageSrc = undefined;
    },

    setAnswerLoading(value: boolean) {
      this.isStartingAnswer = value;
    },

    createBotStageAnswer(value: string) {
      this.socketManager?.check();
      const createBotStageAnswerActionHandler = new CreateBotStageAnswerActionHandler(this);

      createBotStageAnswerActionHandler.handle(value);
    },

    initializeBot() {
      const initializeBotActionHandler = new InitializeBotActionHandler(this);

      initializeBotActionHandler.handle();
    },

    fetchChats(): Promise<void> {
      return FetchChatsActionHandler();
    },

    setActiveChatRoom(chatRoomId?: string): Promise<SetActiveChatReturn> {
      return setActiveChatRoomActionHandler(chatRoomId);
    },

    getChatMessages(chatId: string, unreadEvents: number) {
      return getChatEventsActionHandler(chatId, unreadEvents);
    },

    loadChatRoomUser(chatRoomId: string) {
      const loadUserAh = new LoadChatRoomUserActionHandler(this);

      return loadUserAh.handle(chatRoomId);
    },

    async getMoreOldMessages(): Promise<ChatRoomItemMessageWebSocket[]> {
      if (this.activeChatRoomId !== undefined) {
        const messages = await getChatEventsActionHandler(this.activeChatRoomId);

        if (hasValue(messages)) return messages;

        return [];
      }

      return [];
    },

    sendTextMessage(text: string): Promise<AllowedChatRoomItemType | undefined> {
      if (this.activeChatRoomId !== undefined) {
        const sendTextMessage = new SendTextMessageActionHandler(this);

        return sendTextMessage.handle(this.activeChatRoomId, text);
      }

      return Promise.resolve(undefined);
    },

    sendStatusMessage(status: TYPING_STATUS) {
      if (this.activeChatRoomId !== undefined) {
        const handler = new SentStatusMessageActionHandler(this);

        handler.handle(this.activeChatRoomId, status);
      }
    },
    sendConfirmEvent(chatId: string, messageId: string) {
      ConfirmReceiveMessageActionHandler(chatId, messageId);

      this.rooms[chatId]!.unreadEvents = 0;
    },

    downloadFile(url: string, type: FileTypes, eventId?: string) {
      DownloadFileActionHandler(url, type, eventId);
    },

    setOpenImagePreview(value: boolean) {
      this.openImagePreview = value;
    },

    sendFileMessage(fileQuery: SendFileQuery<File | IonicLocalFile>) {
      if (this.activeChatRoomId !== undefined) {
        if (fileQuery.type === FileTypes.DOCUMENT) {
          const handler = new SendDocumentActionHandler(this);

          handler.handle(fileQuery.file as File, this.activeChatRoomId);
        }

        if (fileQuery.type === FileTypes.IMAGE) {
          SendImageActionHandler(fileQuery.file as File, this.activeChatRoomId);
        }

        if (fileQuery.type === FileTypes.AUDIO) {
          const handler = new SendAudioActionHandler(this);

          handler.handle(fileQuery.file as IonicLocalFile, this.activeChatRoomId);
        }
      }
    },

    setFileProgress(progress: number) {
      if (progress < 100) {
        this.fileProgress = progress;
      } else {
        this.fileProgress = 0;
      }
    },

    showFileError(type: FileTypes) {
      if (type === FileTypes.DOCUMENT) {
        this.notificationMessage = "chat.documents.error.document_error";
      } else if (type === FileTypes.IMAGE) {
        this.notificationMessage = "chat.documents.error.image_error";
      } else if (type === FileTypes.AUDIO) {
        this.notificationMessage = "chat.documents.error.audio_error";
      }

      this.notificationError = true;
    },

    closeNotificationError() {
      this.notificationError = false;
    },

    async sendAndAcceptAllLegalAgreements() {
      const chatRoom = this.getActiveRoom;

      if (hasValue(chatRoom) && this.userStore.getUserIsTherapist && hasValue(this.userStore.user)) {
        this.rooms[chatRoom.id]!.user.isLoadingUser = true;

        const handler = new SendAllLegalAgreementsToPatientActionHandler();

        await handler.handle(chatRoom.user.id, this.userStore.user.id);

        this.loadChatRoomUser(chatRoom.id);
      }
    },

    reloadCurrentBreak(roomId: string) {
      const handler = new ReloadCurrentBreakActionHandler(this);

      handler.handle(roomId);
    },

    clearChatEvents() {
      this.activeChatRoomId = undefined;
      this.chatRooms.forEach((room) => {
        this.rooms[room.id]!.events = [];
      });
    },

    async processEventMessage(event: unknown): Promise<undefined | unknown> {
      let returnedValue: unknown | undefined = undefined;

      const filter = messageFilters.find((f) => f.isType(event));

      if (filter) {
        returnedValue = await filter.handler(event, {});
      }

      return Promise.resolve(returnedValue);
    },
  },
  getters: {
    chatRooms(state): ChatRoom[] {
      return sortChatRooms(state.rooms);
    },

    chatEvents(): AllowedChatRoomItemType[] {
      if (this.activeChatRoomId === undefined) return [];
      if (!this.rooms[this.activeChatRoomId] || !this.rooms[this.activeChatRoomId]!.events) return [];

      const result = this.rooms[this.activeChatRoomId]!.events.filter(
        (ev) => ev.type !== ChatRoomItemType.UNKNOWN
      ) as AllowedChatRoomItemType[];

      return uniqBy(result, (e) => e.id);
    },

    getRoomIdFromPatientUuid: (state) => {
      return (patientUuid: string): string | undefined =>
        Object.keys(state.rooms).find((roomId) => {
          return state.rooms[roomId]!.user.id === patientUuid;
        });
    },

    chatEventsIncludingSystemEvents(): AllChatRoomItemType[] {
      if (this.activeChatRoomId === undefined) return [];
      if (!this.rooms[this.activeChatRoomId] || !this.rooms[this.activeChatRoomId]!.events) return [];

      return this.rooms[this.activeChatRoomId]!.events;
    },

    getActiveRoom(): ChatRoom | undefined {
      if (this.activeChatRoomId === undefined) return undefined;

      const chatRoom: ChatRoom | undefined = this.chatRooms.find(
        (chatRoomId) => chatRoomId.id === this.activeChatRoomId
      );

      if (hasValue(chatRoom)) {
        return chatRoom;
      } else {
        return undefined;
      }
    },

    unreadMessages(): number {
      return this.chatRooms.reduce((acc, room) => acc + room.unreadEvents, 0);
    },

    getFileProgress(state): number {
      return state.fileProgress;
    },

    getIsB2b(): boolean {
      return Boolean(this.getActiveRoom?.user.isB2b);
    },

    getUserActiveRoom(): ChatRoomUser | undefined {
      return this.getActiveRoom?.user;
    },

    getBotStatus(state): BotStageType | undefined {
      return state.botStage?.type;
    },

    isSocketOpen(): boolean {
      return hasValue(this.socketManager) && this.socketManager.isOpen();
    },
  },
});

export type ChatStore = ReturnType<typeof useChatStore>;

export default useChatStore;
